import type { MutationStatus } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { postLogin } from "../../api/index.ts";
import { AUTH_TOKEN_KEY } from "../../constants/index.ts";

type LoginResult = {
  status: MutationStatus;
  error: unknown;
};

/**
 * In addition to logging in the user, this hook also performs the following
 * side effects:
 *
 * - Clears any previous API key from session storage
 * - Invalidates all queries (to remove previous user-specific data)
 * - Navigates to the redirect URL
 *
 * The login mutation is triggered whenever the external ID changes, as long as
 * the mutation is in the idle state.
 */
export function useLogin({
  provider: _provider,
  redirectTo,
  externalId,
}: {
  provider: string;
  redirectTo?: string;
  externalId: string;
}): LoginResult {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, error, status } = useMutation({
    mutationFn: postLogin,
    retry: 2,
    onSuccess: () => {
      // When the user logs in, we want to clear the API key
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      // We also want to refetch all queries. This ensures we have up to date
      // public data as well as user-specific data.
      queryClient
        .invalidateQueries({ queryKey: [] })
        .catch((error) => console.error("Failed to invalidate queries", error));

      if (redirectTo) {
        // We also want to navigate to the redirect URL
        Promise.resolve(navigate(redirectTo, { replace: true })).catch(
          (error) => console.error("Failed to navigate to redirect URL", error),
        );
      }
    },
  });

  useEffect(() => {
    if (externalId && status === "idle") {
      mutate({ id: externalId });
    }
  }, [externalId, status, mutate]);

  return { status, error };
}
